import { sanitizeReturnTo } from "@repo/shared";

// Login hints and dev prefill that only concern the login page itself. They
// must never be forwarded to the API's authorize endpoint.
const LOGIN_ONLY_PARAMS = [
  "tenant",
  "sso",
  "ssoError",
  "ssoRef",
  "ssoKind",
  "ssoMessage",
  "email",
  "password",
];

const SSO_LOGIN_QUERY_KEY = "qp.ssoLoginQuery";

// Bookkeeping the OAuth provider adds when it signs the authorize request into
// the login page's query. Not part of the original authorize request.
const SIGNED_QUERY_PARAMS = ["sig", "exp", "ba_iat", "ba_pl", "ba_param"];

/**
 * True while the login page is part of an OAuth sign-in (an MCP client or QP's own
 * app): the authorize endpoint signed the request into the query (`?client_id=…&sig=…`).
 * Every hop between hosts and pages must then keep that query, or the app never
 * gets its code.
 */
export function isOAuthLogin(searchParams: URLSearchParams): boolean {
  return Boolean(searchParams.get("client_id") && searchParams.get("sig"));
}

/** The login page for the OAuth sign-in in `searchParams`, on the current host. */
export function oauthLoginPath(searchParams: URLSearchParams): string {
  return `/login?${stripLoginOnlyParams(searchParams)}`;
}

/**
 * Where to send the browser once a session exists after login (credentials or
 * 2FA verification).
 *
 * MCP OAuth flow (`?client_id=…&sig=…`): the authorize endpoint sent the user to
 * /login with the OAuth request signed into the query. The user has to return
 * to the authorize endpoint on the API origin so Better Auth can issue the code
 * or show the consent page. A relative `/api/...` path would hit the web origin
 * (the SPA fallback), never the API — which is why this must be absolute. The
 * session cookie is set against the API origin by the sign-in fetch
 * (credentials: include), so a top-level navigation there carries it.
 *
 * The tenant-scoped password sign-in and the 2FA verify page are not Better
 * Auth sign-in endpoints, so the provider cannot resume the flow by itself; an
 * SSO login uses the same URL as its callbackURL.
 */
export function getPostLoginRedirect(searchParams: URLSearchParams): string {
  if (isOAuthLogin(searchParams)) {
    return `${process.env.BUN_PUBLIC_API_URL}/api/auth/oauth2/authorize?${getAuthorizeQuery(searchParams)}`;
  }
  // Origin-locked: the param is attacker-controllable (`/login?redirect=…`), so
  // anything that does not resolve to this app's origin falls back to "/".
  const requested = searchParams.get("redirect") || searchParams.get("callbackUrl");
  return sanitizeReturnTo(requested, window.location.origin) ?? "/";
}

/**
 * The original authorize request from a signed login query: only the signed
 * parameters, without the signing bookkeeping, and without `prompt=login`
 * (the user has just signed in, so asking again would loop back to /login).
 */
function getAuthorizeQuery(searchParams: URLSearchParams): URLSearchParams {
  const signed = new Set(searchParams.getAll("ba_param"));
  const query = new URLSearchParams();
  for (const [key, value] of searchParams) {
    if (!signed.has(key) || SIGNED_QUERY_PARAMS.includes(key)) continue;
    if (key === "prompt") {
      const prompt = value
        .split(" ")
        .filter((p) => p && p !== "login")
        .join(" ");
      if (prompt) query.append(key, prompt);
      continue;
    }
    query.append(key, value);
  }
  return query;
}

/**
 * The login query without the login-only hints. Use this when forwarding the
 * query to another page (e.g. /login/verify-2fa) so dev-prefilled credentials
 * never travel on in the URL.
 */
export function stripLoginOnlyParams(searchParams: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(searchParams);
  for (const key of LOGIN_ONLY_PARAMS) params.delete(key);
  return params;
}

/**
 * Keeps the login continuation (`?redirect=`, MCP OAuth params) across an SSO
 * round trip (QL-348). The SSO error URL cannot carry a query — better-auth
 * appends `?error=` to it as-is — so a failed SSO login would otherwise return
 * to a bare /login and a retry would lose the flow it was part of. Written
 * right before each SSO redirect, so it always reflects the latest attempt.
 */
export function rememberSsoLoginQuery(searchParams: URLSearchParams): void {
  try {
    sessionStorage.setItem(SSO_LOGIN_QUERY_KEY, stripLoginOnlyParams(searchParams).toString());
  } catch {
    // Storage unavailable: a failed SSO login returns to a bare /login.
  }
}

/** The login query remembered before the last SSO redirect in this tab; empty if none. */
export function readSsoLoginQuery(): URLSearchParams {
  try {
    return new URLSearchParams(sessionStorage.getItem(SSO_LOGIN_QUERY_KEY) ?? "");
  } catch {
    return new URLSearchParams();
  }
}
